// ============================================
// AlpenScreen - Add Screen Modal (Standalone)
// File: frontend/src/components/AddScreenModal.jsx
// Purpose: Modal with INTERNAL state - doesn't trigger parent re-renders
// WITH CONNECTION TYPE SUPPORT
// ============================================

import React, { useState } from 'react';
import { X, Monitor, Smartphone, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const AddScreenModal = ({ isOpen, onClose, onScreenAdded }) => {
  const { t } = useTranslation();
  
  // ✅ State HIER drin - nicht im Dashboard!
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [connectionType, setConnectionType] = useState('managed_device'); // ✅ GEÄNDERT: Default to managed_device
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) return;

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/screens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          name: name.trim(),
          location: location.trim() || undefined,
          connectionType // Add connection type
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create screen');
      }

      const data = await response.json();

      // Success!
      setName('');
      setLocation('');
      setConnectionType('managed_device'); // ✅ GEÄNDERT: Reset to managed_device
      onClose();
      onScreenAdded(data); // Pass full response to parent for showing connection info
      
    } catch (error) {
      console.error('Error creating screen:', error);
      alert(t('dashboard.errors.createScreenFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setLocation('');
    setConnectionType('managed_device'); // ✅ GEÄNDERT: Reset to managed_device
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-0">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-2xl m-4 flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {t('dashboard.addScreen')}
          </h3>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Name Input */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                {t('screens.name')} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
                placeholder={t('screens.namePlaceholder')}
                autoFocus
                required
              />
            </div>

            {/* Location Input (Optional) */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                {t('screens.location')} <span className="text-gray-500 text-xs">(optional)</span>
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
                placeholder={t('screens.locationPlaceholder')}
              />
            </div>

            {/* Connection Type Selection */}
            <div>
              <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                {t('screens.connectionType', 'Connection Type')} *
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* ✅ GEÄNDERT: Managed Device Option - NOW FIRST AND DEFAULT */}
                <button
                  type="button"
                  onClick={() => setConnectionType('managed_device')}
                  className={`
                    p-5 rounded-xl border-2 transition-all text-left
                    ${connectionType === 'managed_device'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
                    }
                  `}
                >
                  <div className="flex items-start gap-3">
                    <div className={`
                      p-2.5 rounded-lg
                      ${connectionType === 'managed_device'
                        ? 'bg-primary-600 dark:bg-primary-600'
                        : 'bg-gray-200 dark:bg-gray-600'
                      }
                    `}>
                      <Smartphone className="w-5 h-5 text-white" />
                    </div>

                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        {t('screens.managedDevice', 'Managed Device')}
                        
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {t('screens.managedDeviceDesc', 'Easy pairing with PIN code')}
                      </p>

                      <div className="space-y-1 text-xs text-gray-500 dark:text-gray-500">
                        <div className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          {t('screens.managedPro1', 'Quick setup with PIN')}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          {t('screens.managedPro2', 'QR code support')}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          {t('screens.managedPro3', 'Device tracking')}
                        </div>
                      </div>
                    </div>
                  </div>
                </button>

                {/* ✅ GEÄNDERT: Direct URL Option - NOW SECOND, NO RECOMMENDED BADGE */}
                <button
                  type="button"
                  onClick={() => setConnectionType('direct_url')}
                  className={`
                    p-5 rounded-xl border-2 transition-all text-left
                    ${connectionType === 'direct_url'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
                    }
                  `}
                >
                  <div className="flex items-start gap-3">
                    <div className={`
                      p-2.5 rounded-lg
                      ${connectionType === 'direct_url'
                        ? 'bg-primary-600 dark:bg-primary-600'
                        : 'bg-gray-200 dark:bg-gray-600'
                      }
                    `}>
                      <Monitor className="w-5 h-5 text-white" />
                    </div>

                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        {t('screens.directUrl', 'Direct URL')}
                        {/* ❌ ENTFERNT: Recommended Badge */}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {t('screens.directUrlDesc', 'Permanent link for fixed installations')}
                      </p>

                      <div className="space-y-1 text-xs text-gray-500 dark:text-gray-500">
                        <div className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          {t('screens.directPro1', 'No expiration')}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          {t('screens.directPro2', 'Set and forget')}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          {t('screens.directPro3', 'Perfect for kiosk mode')}
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Info Box */}
              <div className="mt-4 flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700 dark:text-blue-300">
                  {connectionType === 'managed_device' ? (
                    <p>{t('screens.managedInfo', 'You\'ll receive a PIN code to connect your device. Perfect for temporary setups, demos, or when you want device tracking.')}</p>
                  ) : (
                    <p>{t('screens.directInfo', 'You\'ll receive a permanent URL to configure your device. Ideal for fixed installations and kiosk setups.')}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!name.trim() || isSubmitting}
            className="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
          >
            {isSubmitting ? t('common.saving') : t('dashboard.addScreen')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddScreenModal;